/**
 * PendingBorrowRequestsForItem — FE-249.
 *
 * Inline ItemDetail card showing borrow-request state for the viewer's
 * relationship to this specific item. Three mutually-exclusive states:
 *
 *  1. **Owner view, ≥1 PENDING/WAITLISTED row** — renders one card per
 *     request with inline [Approve] / [Decline]. Same component the
 *     GroupDetail section uses (PendingRequestCard) so UX stays
 *     identical across surfaces.
 *
 *  2. **Requester view, viewer's own PENDING/WAITLISTED row exists** —
 *     renders a single chip "Your request is pending" with [Cancel
 *     request] button. Fires cancelSentRequest on tap.
 *
 *  3. **None of the above** — component returns null (nothing rendered).
 *
 * FE-233's state-banner work models the 6 share/custody states. This
 * card adds a 7th informational surface specifically for borrow
 * requests — explicitly NOT folded into the banner because borrow
 * requests aren't a "state" of the item itself; they're queued
 * requests from third parties.
 *
 * Slot placement on ItemDetail: above "Hand off to group member" and
 * the "Shared with" card, so it surfaces immediately when the viewer
 * lands on the page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';

import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  cancelSentRequest,
  loadReceivedRequests,
  loadSentRequests,
  selectReceivedRequests,
  selectSentRequests,
} from '../../borrowRequests/borrowRequestsSlice';
import { PendingRequestCard } from '../../borrowRequests/components/PendingRequestCard';
import { selectUser } from '../../auth/authSlice';

export interface PendingBorrowRequestsForItemProps {
  /** The item being viewed. We filter borrow-request rows by `item_id`. */
  itemId: number;
  /** Owner of this item. Drives owner-view vs requester-view branching;
   *  when undefined, the component is conservative and renders nothing
   *  to avoid showing an action a viewer isn't authorized for. */
  itemOwnerUserId?: number | null;
}

export const PendingBorrowRequestsForItem: React.FC<
  PendingBorrowRequestsForItemProps
> = ({ itemId, itemOwnerUserId }) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const allReceived = useAppSelector(selectReceivedRequests);
  const allSent = useAppSelector(selectSentRequests);

  const viewerId = currentUser?.id ?? null;
  const viewerIsOwner =
    viewerId != null && itemOwnerUserId != null && viewerId === itemOwnerUserId;

  // Refetch on mount so the cache is fresh when the user lands.
  // Both calls are cheap + idempotent (already used app-wide).
  useEffect(() => {
    if (viewerIsOwner) {
      dispatch(loadReceivedRequests({}) as any);
    } else {
      dispatch(loadSentRequests() as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, viewerIsOwner]);

  // ── Branch 1: OWNER view, pending requests on this item ────────────
  const ownerPending = useMemo(() => {
    if (!viewerIsOwner) return [];
    return (allReceived ?? [])
      .filter((r) => {
        if ((r as any).item_id !== itemId) return false;
        const status = String((r as any).status ?? '').toUpperCase();
        return status === 'PENDING' || status === 'WAITLISTED';
      })
      .sort((a, b) => {
        const ta = new Date(a.created_at ?? 0).getTime();
        const tb = new Date(b.created_at ?? 0).getTime();
        return tb - ta;
      });
  }, [allReceived, viewerIsOwner, itemId]);

  // ── Branch 2: REQUESTER view, viewer's own outgoing request ────────
  const requesterPending = useMemo(() => {
    if (viewerIsOwner) return null;
    return (
      (allSent ?? []).find((r) => {
        const status = String((r as any).status ?? '').toUpperCase();
        return (
          (r as any).item_id === itemId &&
          (status === 'PENDING' || status === 'WAITLISTED')
        );
      }) ?? null
    );
  }, [allSent, viewerIsOwner, itemId]);

  // ── Render ─────────────────────────────────────────────────────────
  if (viewerIsOwner) {
    if (ownerPending.length === 0) return null;
    return (
      <View style={styles.section} testID={`item-pending-requests-${itemId}`}>
        <View style={styles.header}>
          <MaterialIcons
            name="hourglass-empty"
            size={16}
            color={fpColors.primary}
          />
          <Text style={styles.headerTitle}>
            {ownerPending.length === 1
              ? '1 pending request'
              : `${ownerPending.length} pending requests`}
          </Text>
        </View>
        {ownerPending.map((req) => (
          <PendingRequestCard
            key={req.id}
            request={req}
            variant="item"
            /* No onPressBody here — we're already on the item page. */
          />
        ))}
      </View>
    );
  }

  // Branch 2 — requester view. `sentRequests` is typed as
  // `BorrowRequestResponse[]` (the creation-time shape) but the BE
  // returns the richer `BorrowRequestDetails` shape at runtime, hence
  // the wider cast for the fields we need.
  if (requesterPending) {
    const row = requesterPending as unknown as {
      id?: number;
      request_id?: number;
      created_at?: string | null;
      group_name?: string | null;
    };
    const reqId = (row.id ?? row.request_id) as number | undefined;
    if (reqId == null) return null;
    return (
      <RequesterPendingChip
        requestId={reqId}
        createdAt={row.created_at ?? null}
        groupName={row.group_name ?? null}
      />
    );
  }

  return null;
};

// ── Requester-side chip ─────────────────────────────────────────────

interface RequesterPendingChipProps {
  requestId: number;
  createdAt: string | null | undefined;
  groupName: string | null;
}

const RequesterPendingChip: React.FC<RequesterPendingChipProps> = ({
  requestId,
  createdAt,
  groupName,
}) => {
  const dispatch = useAppDispatch();
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await dispatch(cancelSentRequest({ requestId }) as any).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Request cancelled',
        text2: 'The owner has been notified.',
      });
      // Slice removes the row from sentRequests, so the chip unmounts
      // naturally on the next render.
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: "Couldn't cancel",
        text2:
          (typeof err === 'string' ? err : err?.message) ??
          'Please try again.',
      });
      setCancelling(false);
    }
  };

  const timeAgoLabel = formatTimeAgo(createdAt);
  const subtitle = groupName
    ? `Asked via ${groupName}${timeAgoLabel ? ` · ${timeAgoLabel}` : ''}`
    : timeAgoLabel || 'Awaiting owner response';

  return (
    <View style={styles.requesterChip} testID={`item-my-pending-${requestId}`}>
      <View style={styles.requesterChipLeft}>
        <MaterialIcons
          name="hourglass-empty"
          size={18}
          color={
            (fpColors as any).onPrimaryContainer ?? fpColors.primary
          }
        />
        <View style={{ flex: 1 }}>
          <Text style={styles.requesterChipTitle}>Your request is pending</Text>
          <Text style={styles.requesterChipSubtitle}>{subtitle}</Text>
        </View>
      </View>
      <Pressable
        onPress={handleCancel}
        disabled={cancelling}
        style={({ pressed }) => [
          styles.cancelBtn,
          pressed && { opacity: 0.85 },
          cancelling && { opacity: 0.6 },
        ]}
        accessibilityRole="button"
        accessibilityLabel="Cancel your borrow request"
        testID={`item-my-pending-cancel-${requestId}`}
      >
        {cancelling ? (
          <ActivityIndicator size="small" color={fpColors.primary} />
        ) : (
          <Text style={styles.cancelBtnText}>Cancel request</Text>
        )}
      </Pressable>
    </View>
  );
};

// ── helpers ──────────────────────────────────────────────────────────

function formatTimeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  section: {
    backgroundColor:
      (fpColors as any).surfaceContainerLowest ?? fpColors.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    padding: 14,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  headerTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  requesterChip: {
    backgroundColor:
      (fpColors as any).primaryContainer ??
      fpColors.surfaceVariant ??
      '#EADDFF',
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginHorizontal: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  requesterChipLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  requesterChipTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: (fpColors as any).onPrimaryContainer ?? fpColors.onSurface,
  },
  requesterChipSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: (fpColors as any).onPrimaryContainer ?? fpColors.onSurfaceVariant,
    marginTop: 1,
  },
  cancelBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.primary,
  },
  cancelBtnText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
});
